import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircledIcon, PlusIcon } from '@radix-ui/react-icons';
import { Button, Spinner } from '../../../../components';
import { useAuthStore } from '../../../../store/authStore';
import { kanbanService } from '../../../../services/kanbanService';
import {
  formatUserTaskDueDate,
  formatUserTaskPriority,
  USER_TASK_STATUS_LABEL,
  userTasksService,
  userTaskStatusCssKey,
  userTaskSubtitle,
} from '../../../../services/userTasksService';
import type { CreateUserTaskPayload, UserTaskCard } from '../../../../types/userTask';
import type { CreateKanbanTaskRequest, KanbanTask } from '../../../../types/kanban';
import { formatDueDateForApi } from '../../../../utils/taskDate';
import { TaskDetailsModal } from '../../../kanban/TaskDetailsModal';
import './ClientTasksTab.scss';

interface ClientTasksTabProps {
  clientUserId: string;
  clientName?: string;
  clientEmail?: string;
  onCountChange?: (count: number) => void;
}

const buildCreateUserTaskPayload = (
  payload: CreateKanbanTaskRequest
): CreateUserTaskPayload | undefined => {
  const body: CreateUserTaskPayload = {};

  if (payload.taskTypeId) {
    body.taskTypeId = payload.taskTypeId;
  }
  if (payload.priority) {
    body.priority = payload.priority;
  }
  if (payload.dueDate) {
    const apiDueDate = formatDueDateForApi(payload.dueDate);
    if (apiDueDate) {
      body.dueDate = apiDueDate;
    }
  }
  if (payload.assigneeId) {
    body.assigneeUserId = Number(payload.assigneeId);
  }
  if (payload.reporterId) {
    body.reporterUserId = Number(payload.reporterId);
  }
  if (payload.labelIds?.length) {
    body.labelIds = payload.labelIds;
  }

  return Object.keys(body).length > 0 ? body : undefined;
};

export const ClientTasksTab: React.FC<ClientTasksTabProps> = ({
  clientUserId,
  clientName,
  clientEmail,
  onCountChange,
}) => {
  const navigate = useNavigate();
  const hasPermission = useAuthStore((state) => state.hasPermission);

  const [tasks, setTasks] = useState<UserTaskCard[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const createTaskPreset = useMemo<KanbanTask | null>(() => {
    if (!clientName && !clientEmail) {
      return null;
    }

    return {
      taskId: '',
      taskNumber: '',
      columnId: 'un_assigned',
      title: clientName ?? '',
      email: clientEmail ?? '',
      leadUserId: clientUserId,
      priority: null,
      labels: [],
      typeName: null,
      dueDate: null,
      order: 0,
    };
  }, [clientEmail, clientName, clientUserId]);

  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const rows = await userTasksService.listTasks(clientUserId);
      setTasks(rows);
      onCountChange?.(rows.length);
    } catch (err) {
      setError((err as { message?: string })?.message ?? 'Failed to load tasks');
      onCountChange?.(0);
    } finally {
      setIsLoading(false);
    }
  }, [clientUserId, onCountChange]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const openTask = useCallback(
    (taskId: string | number) => {
      navigate('/kanban', { state: { openTaskId: String(taskId) } });
    },
    [navigate]
  );

  const handleOpenCreateModal = useCallback(() => {
    setError(null);
    setIsTaskModalOpen(true);
  }, []);

  const handleCloseCreateModal = useCallback(() => {
    if (isSavingTask) return;
    setIsTaskModalOpen(false);
  }, [isSavingTask]);

  const handleSaveTask = useCallback(
    async (payload: CreateKanbanTaskRequest) => {
      setIsSavingTask(true);
      setError(null);

      try {
        const existingIds = new Set(tasks.map((task) => String(task.taskId)));
        const created = await userTasksService.createTask(
          clientUserId,
          buildCreateUserTaskPayload(payload)
        );
        const createdId = String(created.taskId);

        await kanbanService.updateTask(createdId, {
          title: payload.title,
          email: payload.email,
          description: payload.description,
          columnId: payload.columnId,
          priority: payload.priority ?? null,
          taskTypeId: payload.taskTypeId ?? null,
          dueDate: payload.dueDate ?? null,
          assigneeId: payload.assigneeId ?? null,
          reporterId: payload.reporterId ?? null,
          labelIds: payload.labelIds ?? [],
        });

        if (existingIds.has(createdId)) {
          setToast('Task already exists for this user');
        }

        await loadTasks();
        setIsTaskModalOpen(false);
      } catch (err) {
        const message = (err as { message?: string })?.message ?? 'Failed to create task';
        setError(message);
        throw err;
      } finally {
        setIsSavingTask(false);
      }
    },
    [clientUserId, loadTasks, tasks]
  );

  return (
    <section className="client-tasks-tab">
      <div className="client-tasks-tab__header">
        <h2>
          <CheckCircledIcon aria-hidden="true" />
          Client Tasks
        </h2>
        {hasPermission('task_mgmt:create') && (
          <Button
            size="small"
            className="client-tasks-tab__create-btn"
            onClick={handleOpenCreateModal}
          >
            <PlusIcon aria-hidden="true" />
            Create Task
          </Button>
        )}
      </div>

      {toast && <p className="client-tasks-tab__toast">{toast}</p>}
      {error && <p className="client-tasks-tab__error">{error}</p>}

      <div className="client-tasks-tab__table-wrap">
        {isLoading ? (
          <div className="client-tasks-tab__loading">
            <Spinner size="medium" />
          </div>
        ) : (
          <table className="client-tasks-tab__table">
            <thead>
              <tr>
                <th>Task Name</th>
                <th>Due Date</th>
                <th>Priority</th>
                <th>Assignee</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 ? (
                <tr>
                  <td colSpan={6} className="client-tasks-tab__empty">
                    No tasks yet. Create one to get started.
                  </td>
                </tr>
              ) : (
                tasks.map((task) => {
                  const taskId = String(task.taskId);
                  const priority = formatUserTaskPriority(task.priority ?? undefined);
                  const statusKey = userTaskStatusCssKey(task.status);

                  return (
                    <tr
                      key={taskId}
                      className="client-tasks-tab__row"
                      onClick={() => openTask(task.taskId)}
                    >
                      <td className="client-tasks-tab__name-cell">
                        <strong>{task.title}</strong>
                        <span>{userTaskSubtitle(task)}</span>
                      </td>
                      <td>{formatUserTaskDueDate(task.dueDate)}</td>
                      <td>
                        <span
                          className={`client-tasks-tab__pill client-tasks-tab__pill--priority-${priority.cssKey}`}
                        >
                          {priority.label}
                        </span>
                      </td>
                      <td>{task.assignee?.name ?? '—'}</td>
                      <td>
                        <span
                          className={`client-tasks-tab__pill client-tasks-tab__pill--status-${statusKey}`}
                        >
                          {USER_TASK_STATUS_LABEL[task.status]}
                        </span>
                      </td>
                      <td className="client-tasks-tab__actions-cell">
                        <button
                          type="button"
                          className="client-tasks-tab__view-btn"
                          onClick={(event) => {
                            event.stopPropagation();
                            openTask(task.taskId);
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      <TaskDetailsModal
        isOpen={isTaskModalOpen}
        mode="create"
        task={createTaskPreset}
        defaultColumnId="un_assigned"
        onClose={handleCloseCreateModal}
        onSave={handleSaveTask}
        isSaving={isSavingTask}
      />
    </section>
  );
};
